import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  CalendarIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";

import { useEffect, useState, useRef } from "react";
import swal from "sweetalert";
import { selectCurrentUser } from "../../Features/auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from "react-use-is-online";
import ReactPaginate from "react-paginate";

import "../../Pagination.css";
import "./ManageReceiptsView.css";

import { motion, AnimatePresence } from "framer-motion";

import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";

import { format, addDays, subDays } from "date-fns";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { DateRangePicker } from "react-date-range";

import {
  useGetSalesByCaterotyByDateRangeMutation,
  useGetSalesByCaterotyPageChangeMutation,
  useGetSalesByCaterotyQuery,
} from "../../Features/userReportsSlice";

import {
  selectDateRange,
  setDateRange,
} from "../../Features/date/dateSlice";

import { useTheme } from "../../ThemeContext";

function ManageSaleByCategoryView({}) {
  const { darkMode } = useTheme();

  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);

  const [progress, setProgress] = useState(0);
  const { isOnline } = useIsOnline();

  const [numRowsPerPage, setNumRowsPerPage] = useState(10);
  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [open, setOpen] = useState(false);

  const pickerRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(false);
  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [input, setInput] = useState({
    search: "",
  });

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: "selection",
    },
  ]);

  const initialStartDate = date_range
    ? format(date_range.startDate, "yyyy-MM-dd")
    : format(subDays(new Date(), 7), "yyyy-MM-dd");

  const initialEndDate = date_range
    ? format(date_range.endDate, "yyyy-MM-dd")
    : format(new Date(), "yyyy-MM-dd");

  /*
  |--------------------------------------------------------------------------
  | Initial Data
  |--------------------------------------------------------------------------
  */

  const {
    data,
    isLoading,
    isSuccess,
  } = useGetSalesByCaterotyQuery({
    user_id: user.id,
    pagination: "10",
    start_date: initialStartDate,
    end_date: initialEndDate,
    employee_id: "0",
  });

  /*
  |--------------------------------------------------------------------------
  | Initial Response
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess) {
      setCurrentItems(data?.data?.data || []);

      setPageCount(
        Math.ceil(
          Number(data?.data?.total || 0) / Number(numRowsPerPage || 10)
        )
      );
    }

    setProgress(100);

    const handleClickOutside = (event) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, [data, isLoading, isSuccess]);

  /*
  |--------------------------------------------------------------------------
  | Date Range
  |--------------------------------------------------------------------------
  */

  const [fetchCustomData] =
    useGetSalesByCaterotyByDateRangeMutation();

  const handleSelect = async (ranges) => {
    setCurrentPage(0);

    const { selection } = ranges;

    setState([selection]);

    // First click
    if (!isSelecting) {
      setIsSelecting(true);
      return;
    }

    // Second click
    setIsSelecting(false);
    setOpen(false);

    const startDate = format(
      selection.startDate,
      "yyyy-MM-dd"
    );

    const endDate = format(
      selection.endDate,
      "yyyy-MM-dd"
    );

    dispatch(
      setDateRange({
        date_range: selection,
      })
    );

    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);

    try {
      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        employee_id: "0",
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(
            Number(resp.data.total || 0) /
              Number(resp.data.per_page || numRowsPerPage)
          )
        );
      } else {
        swal("No results found.");
      }
    } catch (err) {
      swal("Error", "Data not found");
    } finally {
      setProgress(100);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CSV Export
  |--------------------------------------------------------------------------
  */

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const employee_id = "0";

      const startDate =
        selectedStartDate || initialStartDate;

      const endDate =
        selectedEndDate || initialEndDate;

      const response = await fetch(
        `http://127.0.0.1:8000/user/sales-by-category/${user.id}/1000/${startDate}/${endDate}`
      );

      if (!response.ok) {
        throw new Error(
          `Server Error: ${response.status}`
        );
      }

      const result = await response.json();

      if (
        !result.success ||
        !result.data?.data?.length
      ) {
        swal(
          "No Data",
          "No sales records found to export.",
          "info"
        );

        return;
      }

      const reportData = result.data.data;

      const headers = [
        "Category",
        "Receipts sold",
        "Net Sales",
        "Cost of goods",
        "Gross profit",
        "Margin",
      ];

      const rows = reportData.map((emp) => [
        emp.category,
        emp.receipts_sold,
        emp.net_sales,
        emp.cost_of_goods,
        emp.gross_profit,
        emp.margin,
      ]);

      const csvContent = [headers, ...rows]
        .map((row) =>
          row
            .map(
              (value) =>
                `"${String(value ?? "").replace(
                  /"/g,
                  '""'
                )}"`
            )
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `sales_by_category_report_${
        new Date().toISOString().split("T")[0]
      }.csv`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      swal(
        "Success",
        "Sales by category report exported successfully.",
        "success"
      );
    } catch (error) {
      console.error("CSV Export Error:", error);

      swal(
        "Error",
        error.message ||
          "Something went wrong while exporting sales report.",
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Pagination
  |--------------------------------------------------------------------------
  */

  const [pageChange] =
    useGetSalesByCaterotyPageChangeMutation();

  const onChangePageRows = async (rows) => {
    setCurrentPage(0);

    const nunRows = window.parseInt(rows);

    setNumRowsPerPage(nunRows);

    const startDate =
      selectedStartDate || initialStartDate;

    const endDate =
      selectedEndDate || initialEndDate;

    try {
      setProgress(80);

      const resp = await pageChange({
        user_id: user.id,
        pagination: nunRows,
        start_date: startDate,
        end_date: endDate,
        page: 1,
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(
            Number(resp.data.total || 0) /
              Number(resp.data.per_page || nunRows)
          )
        );

        setNumRowsPerPage(nunRows);

        setProgress(100);
      }
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        err.message || "An unexpected error occurred.",
        "error"
      );
    }
  };

  const handlePageChange = async (currentPage) => {
    setCurrentPage(currentPage);

    const pageNum =
      window.parseInt(currentPage) + 1;

    const startDate =
      selectedStartDate || initialStartDate;

    const endDate =
      selectedEndDate || initialEndDate;

    try {
      setProgress(80);

      const resp = await pageChange({
        user_id: user.id,
        pagination: numRowsPerPage,
        start_date: startDate,
        end_date: endDate,
        employee_id: "0",
        page: pageNum,
      }).unwrap();

      if (resp.success) {
        setCurrentItems(resp.data.data);

        setPageCount(
          Math.ceil(
            Number(resp.data.total || 0) /
              Number(resp.data.per_page || numRowsPerPage)
          )
        );

        setProgress(100);
      }
    } catch (err) {
      setProgress(100);

      swal(
        "Error",
        "An expected error. Try again later"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Formatting
  |--------------------------------------------------------------------------
  */

  const formatMoney = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "0.00";
    }

    return number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "0.00";
    }

    return number.toFixed(2);
  };

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />

      <div className="w-full px-3 sm:px-4 lg:px-6 pt-4">

        {/* ============================================================
            HEADER
        ============================================================ */}

        <div className="mb-5">

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

            <div className="flex items-center gap-3">

              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20">

                <ChartBarIcon className="w-6 h-6 text-[#80b643]" />

              </div>

              <div>

                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Sales by Category
                </h1>

                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  View sales performance grouped by category
                </p>

              </div>

            </div>

          </div>

        </div>

        {/* ============================================================
            FILTER CARD
        ============================================================ */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm mb-5">

          <div className="p-4">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

              <div>

                <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  Report period
                </p>

                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-1">
                  Select a date range to update the report
                </p>

              </div>

              {/* Date picker */}

              <div
                ref={pickerRef}
                className="relative w-full sm:w-auto"
              >

                <button
                  type="button"
                  onClick={() => setOpen(!open)}
                  className="
                    w-full sm:w-auto
                    flex items-center justify-between sm:justify-start
                    gap-3
                    bg-gray-50 dark:bg-gray-800
                    border border-gray-200 dark:border-gray-700
                    hover:border-[#80b643]
                    text-gray-700 dark:text-gray-200
                    px-4 py-2.5
                    rounded-lg
                    text-sm
                    transition
                  "
                >

                  <div className="flex items-center gap-2">

                    <CalendarIcon className="w-4 h-4 text-[#80b643]" />

                    <span>
                      {format(
                        date_range
                          ? date_range.startDate
                          : state[0].startDate,
                        "MMM dd, yyyy"
                      )}

                      <span className="mx-2 text-gray-400">
                        →
                      </span>

                      {format(
                        date_range
                          ? date_range.endDate
                          : state[0].endDate,
                        "MMM dd, yyyy"
                      )}
                    </span>

                  </div>

                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform ${
                      open ? "rotate-180" : ""
                    }`}
                  />

                </button>

                <AnimatePresence>

                  {open && (

                    <motion.div
                      initial={{
                        opacity: 0,
                        y: -8,
                        scale: 0.98,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1,
                      }}
                      exit={{
                        opacity: 0,
                        y: -8,
                        scale: 0.98,
                      }}
                      transition={{
                        duration: 0.2,
                      }}
                      className="
                        absolute
                        z-50
                        right-0
                        mt-2
                        bg-white
                        dark:bg-gray-900
                        border
                        border-gray-200
                        dark:border-gray-700
                        rounded-xl
                        shadow-2xl
                        overflow-hidden
                      "
                    >

                      <DateRangePicker
                        onChange={handleSelect}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={state}
                        direction="horizontal"
                      />

                    </motion.div>

                  )}

                </AnimatePresence>

              </div>

            </div>

          </div>

        </div>

        {/* ============================================================
            TABLE / EMPTY STATE
        ============================================================ */}

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

          {currentItems && currentItems.length > 0 ? (

            <>

              {/* Table header */}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">

                <div>

                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Category Performance
                  </h2>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {currentItems.length} categories displayed
                  </p>

                </div>

                <button
                  type="button"
                  onClick={handleExportCSV}
                  disabled={isExporting}
                  className={`
                    inline-flex
                    items-center
                    justify-center
                    gap-2
                    px-4
                    py-2
                    rounded-lg
                    text-sm
                    font-medium
                    border
                    transition
                    ${
                      isExporting
                        ? `
                          bg-gray-200
                          dark:bg-gray-800
                          text-gray-400
                          dark:text-gray-500
                          border-gray-200
                          dark:border-gray-700
                          cursor-not-allowed
                        `
                        : `
                          bg-[#80b643]
                          hover:bg-[#719f3a]
                          text-white
                          border-[#80b643]
                          shadow-sm
                        `
                    }
                  `}
                >

                  <PrinterIcon className="w-4 h-4" />

                  {isExporting
                    ? "Exporting..."
                    : "Export CSV"}

                </button>

              </div>

              {/* Responsive table */}

              <div className="overflow-x-auto">

                <table className="min-w-[750px] w-full">

                  <thead>

                    <tr className="bg-gray-50 dark:bg-gray-800/70 border-b border-gray-100 dark:border-gray-800">

                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Category
                      </th>

                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Receipts Sold
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Net Sales
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Cost of Goods
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Gross Profit
                      </th>

                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Margin
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">

                    {currentItems.map((item, index) => (

                      <tr
                        key={
                          item.employee_id ||
                          item.category_id ||
                          index
                        }
                        className="
                          group
                          hover:bg-gray-50
                          dark:hover:bg-gray-800/60
                          transition
                        "
                      >

                        {/* Category */}

                        <td className="px-5 py-4">

                          <div className="flex items-center gap-3">

                            <div className="w-8 h-8 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center flex-shrink-0">

                              <span className="text-xs font-semibold text-[#80b643]">
                                {(item.category || "N")
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>

                            </div>

                            <div>

                              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">

                                {item.category ||
                                  "No category"}

                              </p>

                            </div>

                          </div>

                        </td>

                        {/* Receipts */}

                        <td className="px-5 py-4">

                          <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium">

                            {Number(
                              item.receipts_sold || 0
                            ).toLocaleString("en-US")}

                          </span>

                        </td>

                        {/* Net Sales */}

                        <td className="px-5 py-4 text-right">

                          <span className="text-sm text-gray-900 dark:text-white whitespace-nowrap">

                            K {formatMoney(item.net_sales)}

                          </span>

                        </td>

                        {/* Cost */}

                        <td className="px-5 py-4 text-right">

                          <span className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">

                            K {formatMoney(
                              item.cost_of_goods
                            )}

                          </span>

                        </td>

                        {/* Gross Profit */}

                        <td className="px-5 py-4 text-right">

                          <span className="text-sm whitespace-nowrap">

                            K {formatMoney(
                              item.gross_profit
                            )}

                          </span>

                        </td>

                        {/* Margin */}

                        <td className="px-5 py-4 text-right">

                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#80b643]/10 text-[#80b643] text-xs font-semibold whitespace-nowrap">

                            {formatPercent(item.margin)}%

                          </span>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

              {/* ======================================================
                  PAGINATION
              ====================================================== */}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-5 py-4 border-t border-gray-100 dark:border-gray-800">

                <div className="text-xs text-gray-500 dark:text-gray-400">

                  Showing{" "}
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {currentItems.length}
                  </span>{" "}
                  records

                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4">

                  <ReactPaginate
                    breakLabel="..."
                    nextLabel={
                      <ChevronRightIcon className="w-5 h-5" />
                    }
                    previousLabel={
                      <ChevronLeftIcon className="w-5 h-5" />
                    }
                    onPageChange={(e) =>
                      handlePageChange(e.selected)
                    }
                    pageRangeDisplayed={3}
                    pageCount={pageCount}
                    forcePage={currentPage}
                    renderOnZeroPageCount={null}
                    containerClassName="pagination"
                    pageLinkClassName="page-num"
                    previousLinkClassName="controlBtn"
                    nextLinkClassName="controlBtn"
                    activeLinkClassName={`
                      ${
                        darkMode
                          ? ""
                          : "active"
                      }
                      dark:text-white
                      bg-white
                      dark:bg-gray-700
                      hover:dark:text-white
                      hover:dark:bg-gray-600
                    `}
                  />

                  <div className="flex items-center gap-2">

                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Rows per page
                    </span>

                    <select
                      value={numRowsPerPage}
                      onChange={(e) =>
                        onChangePageRows(
                          e.target.value
                        )
                      }
                      className="
                        text-xs
                        bg-white
                        dark:bg-gray-800
                        text-gray-600
                        dark:text-gray-200
                        border
                        border-gray-200
                        dark:border-gray-700
                        rounded-md
                        px-2
                        py-1.5
                        outline-none
                        focus:border-[#80b643]
                      "
                    >

                      <option value="10">
                        10
                      </option>

                      <option value="25">
                        25
                      </option>

                      <option value="50">
                        50
                      </option>

                      <option value="100">
                        100
                      </option>

                    </select>

                  </div>

                </div>

              </div>

            </>

          ) : (

            /* ==========================================================
               EMPTY STATE
            ========================================================== */

            <div className="flex flex-col items-center justify-center py-16 px-5">

              <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">

                <ChartBarIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />

              </div>

              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                No sales records found
              </h3>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-sm mt-1">

                There are no category sales records available
                for the selected date range.

              </p>

            </div>

          )}

        </div>

      </div>

    </div>
  );
}

export default ManageSaleByCategoryView;
